import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import * as Yup from "yup";
import SuccessModal from "views/Components/SuccessModal";
import ErrorModal from "views/Components/ErrorModal";
import WarningModal from "views/Components/WarningModal";
import useLiberarTicketMutation from "../Queries/useLiberarTicketMutation";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const ItemSchema = Yup.object().shape({
  num_ticket_taller: Yup.string()
    .required("N° de ticket es requerido")
    .matches(/^[0-9]+$/, "Debes ingresar valores numéricos"),
});

export default function FreeTicket({ mainCountry }) {
  const liberarMutation = useLiberarTicketMutation();
  const [registroCargado, setRegistroCargado] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const formik = useFormik({
    initialValues: {
      num_ticket_taller: "",
    },
    validationSchema: ItemSchema,
    onSubmit: async (values) => {
      try {
        const ticketId = Number(values.num_ticket_taller);
        await liberarMutation.mutateAsync({ ticketId, countryId: mainCountry?.id });
        setRegistroCargado(ticketId);
        setSuccessOpen(true);
        formik.resetForm();
      } catch (error) {
        if (error.code === "NOT_FOUND") {
          setWarningMessage(
            `No se encontró el ticket N°${Number(values.num_ticket_taller)}, verifique y vuelva a intentar`,
          );
          setWarningOpen(true);
        } else if (error.response?.status === 403) {
          setWarningMessage(
            `El ticket N°${Number(values.num_ticket_taller)} no se encuentra asignado en el taller. Por favor consulte con su administrador.`,
          );
          setWarningOpen(true);
        } else {
          setErrorMessage(
            "Error desde el servidor al liberar un ticket. Comunicarse con soporte",
          );
          setErrorOpen(true);
        }
      }
    },
  });

  return (
    <Box sx={{ width: "100%", height: "60vh" }}>
      <SuccessModal
        handleClose={() => setSuccessOpen(false)}
        open={successOpen}
        message="La operación fue un éxito!"
        message2={`Se liberó correctamente el Ticket N° ${registroCargado}`}
      />
      <WarningModal
        handleClose={() => setWarningOpen(false)}
        open={warningOpen}
        message="Ups, algo ocurrió"
        message2={warningMessage}
      />
      <ErrorModal
        handleClose={() => setErrorOpen(false)}
        open={errorOpen}
        message="Error"
        message2={errorMessage}
      />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          backgroundColor: "#E5F6FD",
          padding: 1.5,
          borderRadius: 1,
          mb: 4,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#0288D1" }} />
        <Stack direction="column">
          <Typography
            sx={{ color: "#014361", fontSize: "14px", fontWeight: 500 }}
          >
            Libera tickets bloqueados por usuarios que ya no están activos en el
            sistema.
          </Typography>
        </Stack>
      </Stack>
      {liberarMutation.isPending && (
        <div className="form_overlay">
          <CircularProgress />
        </div>
      )}
      <form onSubmit={formik.handleSubmit} noValidate>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 2,
            mt: 2,
          }}
        >
          <TextField
            sx={{
              width: "300px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: "8px",
              },
              "& .MuiOutlinedInput-input": {
                backgroundColor: "#fff !important",
                borderRadius: "8px",
                WebkitBoxShadow: "0 0 0 1000px white inset",
              },
            }}
            id="num_ticket_taller"
            label="Buscar"
            placeholder="Ej: #8190 o nombre de técnico"
            value={formik.values.num_ticket_taller}
            required
            onChange={(event) => {
              const { value } = event.target;
              if (/^[0-9]*$/.test(value)) {
                formik.handleChange(event);
              }
            }}
            error={
              formik.touched.num_ticket_taller &&
              Boolean(formik.errors.num_ticket_taller)
            }
            helperText={
              formik.touched.num_ticket_taller &&
              formik.errors.num_ticket_taller
            }
          />

          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#002878",
              textTransform: "uppercase",
              height: "40px",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#002878", color: "#FFFFFF" },
            }}
            disabled={
              liberarMutation.isPending ||
              Object.values(formik.values).some((value) => value === "") ||
              Object.values(formik.errors).some((value) => value !== "")
            }
          >
            Liberar
          </Button>
        </Box>
      </form>
    </Box>
  );
}